import React, { useState, useEffect, useCallback } from 'react';
import { setup2FA, confirm2FA, disable2FA, get2FAStatus } from '../api';

export default function TwoFactorSetup({ authAxios, showFormMessage }) {
  const [status, setStatus] = useState({ enabled: false, pending_setup: false });
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null); // { qr_code, provisioning_uri, secret }
  const [totpCode, setTotpCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisableForm, setShowDisableForm] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get2FAStatus(authAxios);
      setStatus(data);
    } catch {
      showFormMessage?.('error', 'Failed to load 2FA status.');
    } finally {
      setLoading(false);
    }
  }, [authAxios, showFormMessage]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleSetup = async () => {
    setSaving(true);
    try {
      const data = await setup2FA(authAxios);
      setSetupData(data);
    } catch (err) {
      showFormMessage?.('error', err?.response?.data?.detail || 'Failed to start 2FA setup.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (totpCode.length !== 6) return;
    setSaving(true);
    try {
      await confirm2FA(authAxios, totpCode);
      showFormMessage?.('success', 'Two-factor authentication has been enabled.');
      setSetupData(null);
      setTotpCode('');
      loadStatus();
    } catch (err) {
      showFormMessage?.('error', err?.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    if (!disablePassword) return;
    setSaving(true);
    try {
      await disable2FA(authAxios, disablePassword);
      showFormMessage?.('success', 'Two-factor authentication has been disabled.');
      setDisablePassword('');
      setShowDisableForm(false);
      loadStatus();
    } catch (err) {
      showFormMessage?.('error', err?.response?.data?.detail || 'Failed to disable 2FA.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <svg className="animate-spin h-6 w-6 mr-2" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Loading security settings...
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">Security Settings</h2>
        <p className="text-sm text-gray-500 mt-1">
          Manage two-factor authentication for your account.
        </p>
      </div>

      {/* Status card */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              status.enabled ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${
                status.enabled ? 'text-green-600' : 'text-gray-400'
              }`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Two-Factor Authentication (TOTP)
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {status.enabled
                  ? 'Your account is protected with an authenticator app.'
                  : 'Add an extra layer of security by requiring a code from your authenticator app at login.'}
              </p>
              <div className="mt-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  status.enabled
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {status.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>

          <div>
            {status.enabled ? (
              <button
                onClick={() => setShowDisableForm(!showDisableForm)}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-red-300 text-red-600 hover:bg-red-50"
              >
                Disable 2FA
              </button>
            ) : !setupData ? (
              <button
                onClick={handleSetup}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Setting up...' : 'Enable 2FA'}
              </button>
            ) : null}
          </div>
        </div>

        {/* Disable form */}
        {showDisableForm && status.enabled && (
          <form onSubmit={handleDisable} className="mt-6 pt-6 border-t space-y-4">
            <p className="text-sm text-gray-600">
              Enter your current password to disable two-factor authentication.
            </p>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 mb-1">Password</label>
                <input
                  type="password"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Enter your password"
                  value={disablePassword}
                  onChange={e => setDisablePassword(e.target.value)}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={saving || !disablePassword}
                className="px-4 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? 'Disabling...' : 'Confirm Disable'}
              </button>
              <button
                type="button"
                onClick={() => { setShowDisableForm(false); setDisablePassword(''); }}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Setup flow */}
      {setupData && !status.enabled && (
        <div className="bg-white rounded-xl border shadow-sm p-6 space-y-6">
          <h3 className="text-base font-semibold text-gray-900">Set Up Authenticator</h3>

          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-3">
                <strong>Step 1:</strong> Install an authenticator app on your phone
                (Google Authenticator, Microsoft Authenticator, Authy, etc.)
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-3">
                <strong>Step 2:</strong> Scan this QR code with your authenticator app:
              </p>
              <div className="flex justify-center p-4 bg-white rounded-lg border">
                <img
                  src={setupData.qr_code}
                  alt="TOTP QR Code"
                  className="w-48 h-48"
                />
              </div>
              <details className="mt-3">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                  Can't scan the QR code? Enter this key manually
                </summary>
                <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                  <code className="text-sm font-mono text-gray-800 break-all select-all">
                    {setupData.secret}
                  </code>
                </div>
              </details>
            </div>

            <form onSubmit={handleConfirm}>
              <p className="text-sm text-gray-600 mb-3">
                <strong>Step 3:</strong> Enter the 6-digit code from your authenticator to confirm:
              </p>
              <div className="flex gap-3 items-end">
                <div className="flex-1 max-w-xs">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    autoComplete="one-time-code"
                    autoFocus
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-lg text-center tracking-widest font-mono"
                    placeholder="000000"
                    value={totpCode}
                    onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={saving || totpCode.length !== 6}
                  className="px-5 py-2.5 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? 'Verifying...' : 'Verify & Enable'}
                </button>
              </div>
            </form>
          </div>

          <div className="pt-4 border-t">
            <button
              type="button"
              onClick={() => { setSetupData(null); setTotpCode(''); }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel setup
            </button>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium">What is Two-Factor Authentication?</p>
            <p className="mt-1 text-blue-700">
              Two-factor authentication adds security to your account by requiring both your password
              and a time-based code from your phone when logging in. This protects your account even
              if your password is compromised.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
